import { Css, FormSectionProps, UseModalHook } from "@homebound/beam";
import { ObjectState } from "@homebound/form-state";
import { ConfigureBidGroupsStepQuery } from "src/routes/bids/plan-based-bid-package/components/2.ConfigureBidGroupsStep.generated";
import { AddOptionModal } from "src/routes/bids/plan-based-bid-package/components/AddOptionModal";
import { buildOptionChildSection } from "src/routes/bids/plan-based-bid-package/components/OptionCard";
import { PlanBasedBidPackageFormState } from "src/routes/bids/plan-based-bid-package/utils";

type BuildAddOnOptionsSectionParams = {
  formState: ObjectState<PlanBasedBidPackageFormState>;
  dimensionResults: ConfigureBidGroupsStepQuery["potentialBidPlanDimensions"];
  potentialBidPlanOptions: ConfigureBidGroupsStepQuery["bidPackage"]["potentialBidPlanOptions"];
  openModal: UseModalHook["openModal"];
};

/**
 * Builds the "Add-On Options" `FormSection`, with one `childSection` per option group.
 *
 * Drag-to-reorder is handled natively by Beam (see `OptionCard.tsx`'s doc comment) — each
 * child's `orderField` is the group's own `order` field, so dragging mutates it directly and
 * `optionGroups`' `sortBy(order)` reflects the new positions on the next render.
 */
export function buildAddOnOptionsSection(params: BuildAddOnOptionsSectionParams): FormSectionProps {
  const { formState, dimensionResults, potentialBidPlanOptions, openModal } = params;
  const optionGroups = formState.bidPlanGroups.rows
    .filter((g) => !!g.globalOptionId.value)
    .sortBy((g) => g.order.value ?? Infinity);

  function openAddOptionModal() {
    openModal({
      content: (
        <AddOptionModal
          formState={formState}
          dimensionResults={dimensionResults}
          potentialBidPlanOptions={potentialBidPlanOptions}
        />
      ),
    });
  }

  return {
    title: "Add-On Options",
    description:
      "Add options that change scope for this trade. Cost lines populate only for product offerings that include the corresponding add-on.",
    actions: [{ icon: "plus", label: "Add Add-on", variant: "secondary", onClick: openAddOptionModal }],
    fields: optionGroups.isEmpty ? (
      <div css={Css.bgGray100.br8.py3.tac.gray700.$}>No Add-on options added.</div>
    ) : undefined,
    childSections: optionGroups.isEmpty
      ? undefined
      : optionGroups.map((group) =>
          buildOptionChildSection({ formState, group, dimensionResults, potentialBidPlanOptions, openModal }),
        ),
  };
}
